import { supabase } from './supabase';
import { createChatRoom, sendMessage } from './chat';

export interface ReservationData {
  restaurant_id: string;
  date: string;
  time: string;
  party_size: number;
  special_requests?: string;
}

export const createReservation = async (data: ReservationData) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to create a reservation');

    // Create the reservation
    const { data: reservation, error: reservationError } = await supabase
      .from('reservations')
      .insert({
        restaurant_id: data.restaurant_id,
        user_id: user.id,
        date: data.date,
        time: data.time,
        party_size: data.party_size,
        special_requests: data.special_requests,
        status: 'pending'
      })
      .select()
      .single();

    if (reservationError) throw reservationError;
    return reservation;
  } catch (err) {
    console.error('Error creating reservation:', err);
    throw err;
  }
};

export const shareReservationViaChat = async (
  recipientId: string,
  restaurantName: string,
  reservationData: ReservationData
) => {
  try {
    // Create or get existing chat room
    const chatRoomId = await createChatRoom([recipientId]);
    
    // Format the message
    const formattedDate = new Date(reservationData.date).toLocaleDateString();
    const message = `🍽️ I've made a reservation at ${restaurantName} for us!\n\n` +
      `📅 Date: ${formattedDate}\n` +
      `🕒 Time: ${reservationData.time}\n` +
      `👥 Party size: ${reservationData.party_size} people\n` +
      (reservationData.special_requests ? `📝 Special requests: ${reservationData.special_requests}\n\n` : '\n') +
      `Would you like to join me?`;
    
    // Send the message
    await sendMessage(chatRoomId, message);
    
    return { success: true, chatRoomId };
  } catch (err) {
    console.error('Error sharing reservation via chat:', err);
    throw err;
  }
};

export const getReservations = async () => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to view reservations');

    const { data, error } = await supabase
      .from('reservations')
      .select(`
        *,
        restaurant:restaurants(*)
      `)
      .eq('user_id', user.id)
      .order('date', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('Error fetching reservations:', err);
    throw err;
  }
};